import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // primary: shown in the desktop bar. All links are shown in the mobile menu.
  const navLinks = [
    { name: 'Services', path: '/services', primary: true },
    { name: 'Lead Generation', path: '/ads-strategy', primary: true },
    { name: 'AI Video', path: '/ai-video-creation-service', primary: true },
    { name: 'Projects', path: '/projects', primary: true },
    { name: 'Process', path: '/process' },
    { name: 'About', path: '/about' },
    { name: 'Join DigitalGigs', path: '/opportunities' },
    { name: 'Affiliates', path: '/affiliates' },
    { name: 'Blog', path: '/blog/', primary: true },
    { name: 'FAQ', path: '/faq', primary: true },
  ];

  const handleNavClick = (path) => {
    setIsOpen(false);
    // The blog is made of static pages outside the React app, so load it with a full page navigation.
    if (path.startsWith('/blog')) {
      window.location.href = path;
      return;
    }
    navigate(path);
  };

  return (
    <nav
      className={`fixed z-50 w-full transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 py-4 shadow-sm backdrop-blur-md'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="flex h-16 flex-shrink-0 items-center mix-blend-multiply transition-transform hover:scale-105 md:h-20"
          >
            <img
              src="https://media-manager-c.questera.ai/greta-media/fafed1dc96bdeaff472e762fc9535f37237b73cd078ba929908d8fc9f91ee4faeb3717965c5074a04f18e80ee0d17e20/images/aW1hZ2UvcG5n/64321200e219a387529c007d184ece45.png"
              alt="DigitalGigs"
              className="h-full w-auto origin-left scale-150 object-contain object-left"
            />
          </Link>

          <div className="hidden items-center space-x-6 lg:flex xl:space-x-8">
            {navLinks.filter((link) => link.primary).map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link.path)}
                className={`whitespace-nowrap text-sm font-bold uppercase tracking-widest transition-colors ${
                  location.pathname === link.path
                    ? 'text-emerald-600'
                    : 'text-slate-600 hover:text-emerald-600'
                }`}
              >
                {link.name}
              </button>
            ))}

            <a
              href="https://go.digitalgigs.eu/chat"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap rounded-full bg-emerald-600 px-8 py-3.5 font-bold text-white shadow-lg shadow-emerald-600/20 transition-all hover:bg-emerald-700 active:scale-95"
            >
              Get a Quote
            </a>
          </div>

          <div className="flex items-center lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="relative z-50 p-2 text-slate-600 hover:text-emerald-600 focus:outline-none"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="h-8 w-8" />
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="absolute max-h-[calc(100vh-5rem)] w-full animate-in slide-in-from-top overflow-y-auto overscroll-contain border-t border-slate-100 bg-white shadow-2xl duration-300 lg:hidden">
          <div className="space-y-1 px-6 pb-10 pt-2">
            {[...navLinks.filter((link) => link.primary), ...navLinks.filter((link) => !link.primary)].map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link.path)}
                className={`block w-full border-b border-slate-50 py-3 text-left text-lg font-bold ${
                  location.pathname === link.path
                    ? 'text-emerald-600'
                    : 'text-slate-900'
                }`}
              >
                {link.name}
              </button>
            ))}

            <div className="pt-6">
              <a
                href="https://go.digitalgigs.eu/chat"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full rounded-[1.5rem] bg-emerald-600 px-6 py-5 text-center text-lg font-bold text-white"
              >
                Get a Free Quote
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
